import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import BackdropPrimaryMain from "components/BackdropPrimaryMain";
import PrimaryButton from "components/PrimaryButton";
import StyledTextField from "components/StyledTextField";
import Transition from "components/Transition";
import { PropertyValueInputType } from "operations/schema/schema";
import { FC, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useAppDispatch, useAppSelector } from "store";
import { selectPageSelectedJob } from "store/root.store";
import { setOpen } from "store/slices/dialog.store";
import { addSnackbarMessage } from "store/slices/snackbar.store";

type EditMachinePropertiesDialogProps = {
  loading: boolean;
  updateMachineProperties: (
    values: PropertyValueInputType[]
  ) => Promise<{ success: boolean; queued: boolean; abort: boolean }>;
};

export const EditMachinePropertiesDialog: FC<EditMachinePropertiesDialogProps> = (props) => {
  const { loading, updateMachineProperties } = props;
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const job = useAppSelector(selectPageSelectedJob);
  const open = useAppSelector((s) => s.dialog.editMachineProperties);
  const [properties, setProperties] = useState(job.machine?.details?.customProperties ?? []);

  const handleClose = useCallback(() => {
    dispatch(setOpen({ dialogName: "editMachineProperties", open: false }));
  }, [dispatch]);

  const updateMachineProps = useCallback(
    async (val: PropertyValueInputType[]) => {
      const ret = await updateMachineProperties(val);
      if (ret.success) {
        handleClose();
        if (ret.queued) {
          dispatch(addSnackbarMessage({ key: "UpdateMachineCustomProps-stored" }));
        } else {
          dispatch(addSnackbarMessage({ key: "UpdateMachineCustomProps-success" }));
        }
      } else if (!ret.success && !ret.abort) {
        dispatch(addSnackbarMessage({ key: "UpdateMachineCustomProps-fail" }));
      }
    },
    [dispatch, handleClose, updateMachineProperties]
  );

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={handleClose}
      TransitionComponent={Transition}
      data-testid="EditMachinePropertiesDialog"
    >
      <BackdropPrimaryMain open={loading}>
        <CircularProgress color="inherit" />
      </BackdropPrimaryMain>
      <AppBar position="fixed">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleClose}
            aria-label={intl.formatMessage({ id: "general.back" })}
            size="large"
            data-testid="EditMachinePropertiesDialog-CloseButton"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography className="AppBar-label" paddingRight="36px">
            <FormattedMessage id="visit.editMachineProperties" />
          </Typography>
        </Toolbar>
      </AppBar>
      <Toolbar />
      <Card>
        <CardContent>
          <Grid container direction="column" spacing={1}>
            {properties.map((cp, index) => (
              <Grid
                key={`${cp?.code}-${index}`}
                item
                container
                justifyContent="space-between"
                alignItems="center"
                wrap="nowrap"
              >
                <Grid item>{cp?.description}</Grid>
                <Grid item xs={5}>
                  <StyledTextField
                    value={cp?.value}
                    size="small"
                    onChange={(e) =>
                      setProperties(
                        properties.map((p, i) =>
                          i === index ? { ...p, value: e.target.value } : p
                        )
                      )
                    }
                    data-testid={`EditMachinePropertiesDialog-Input-${cp.code}`}
                  />
                </Grid>
              </Grid>
            ))}
            <Grid item>
              <PrimaryButton
                key={`${loading}`}
                disabled={loading}
                fullWidth
                onClick={() => {
                  updateMachineProps(
                    properties.filter((p) => p.propertyGuid || p.value) as PropertyValueInputType[]
                  );
                }}
                data-testid="EditMachinePropertiesDialog-SaveButton"
              >
                <FormattedMessage id="general.save" />
              </PrimaryButton>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Dialog>
  );
};
